import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faEye, faPlus, faBan } from '@fortawesome/free-solid-svg-icons'

import { DashboardLayout } from '../../Components/Layout/DashboardLayout'
import CustomTable from '../../Components/CustomTable'
import CustomButton from '../../Components/CustomButton'
import CustomModal from '../../Components/CustomModal'
import EditAdminModal from './EditAdminModal'
import { useGetAdminsQuery, useDeactivateAdminMutation } from '../../Redux/Apis/Admin'

import './style.css'

const AdminManagement = () => {
  const navigate = useNavigate()
  const { user } = useSelector(state => state.authSlice)

  const { data, isLoading, refetch } = useGetAdminsQuery()
  const [deactivateAdmin] = useDeactivateAdminMutation()

  const [showDeactivateModal, setShowDeactivateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState(null)

  const isSuperAdmin = user?.role === 'super_admin'

  const handleDeactivate = (admin) => {
    setSelectedAdmin(admin)
    setShowDeactivateModal(true)
  }

  const handleEdit = (admin) => {
    setSelectedAdmin(admin)
    setShowEditModal(true)
  }

  const confirmDeactivate = async () => {
    try {
      await deactivateAdmin(selectedAdmin._id).unwrap()
      toast.success('Admin deactivated successfully')
      refetch()
      setShowDeactivateModal(false)
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to deactivate admin')
    }
  }

  const handleEditSuccess = () => {
    refetch()
  }

  const getRoleBadge = (role) => {
    return (
      <span className={`badge ${role === 'super_admin' ? 'bg-primary' : 'bg-info'}`}>
        {role === 'super_admin' ? 'Super Admin' : 'Neighborhood Admin'}
      </span>
    )
  }

  const headers = [
    {
      key: 'name',
      title: 'Admin Name'
    },
    {
      key: 'role',
      title: 'Role'
    },
    {
      key: 'neighborhoods',
      title: 'Assigned Neighborhoods'
    },
    {
      key: 'default',
      title: 'Default'
    },
    {
      key: 'status',
      title: 'Status'
    },
    {
      key: 'lastLogin',
      title: 'Last Login'
    },
    {
      key: 'actions',
      title: 'Actions'
    }
  ]

  // Non-super admins can't access this page
  if (!isSuperAdmin) {
    return (
      <DashboardLayout>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="alert alert-danger">
                You do not have permission to view this page. Only super admins can manage admins.
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-12">
            <div className="dashCard">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mainTitle">Admin Management</h2>
                <CustomButton
                  variant="primary"
                  onClick={() => navigate('/admins/create')}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add Admin
                </CustomButton>
              </div>

              <div className="alert alert-info mb-3">
                <strong>Super Admins</strong> can access all neighborhoods and manage the system.
                <br />
                <strong>Neighborhood Admins</strong> can only access their assigned neighborhoods.
              </div>

              <CustomTable
                headers={headers}
                length={data?.data?.length || 0}
              >
                <tbody>
                  {(data?.data || []).map((row, index) => (
                    <tr key={row._id}>
                      <td>
                        <div className="d-flex align-items-center">
                          {row.picture && (
                            <img
                              src={row.picture}
                              alt={row.name}
                              className="admin-avatar me-2"
                            />
                          )}
                          <div>
                            <div className="fw-bold">{row.name}</div>
                            <small className="text-muted">{row.email}</small>
                          </div>
                        </div>
                      </td>
                      <td>{getRoleBadge(row.role)}</td>
                      <td>
                        {row.role === 'super_admin' ? (
                          <span className="text-success">All Neighborhoods</span>
                        ) : (
                          <div>
                            {(row.assignedNeighborhoods || []).length > 0 ? (
                              <>
                                {row.assignedNeighborhoods.map(n => (
                                  <div key={n._id} className="badge bg-secondary me-1 mb-1">
                                    {n.name}
                                  </div>
                                ))}
                              </>
                            ) : (
                              <span className="text-muted">None assigned</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {row.defaultNeighborhood ? (
                          <span className="badge bg-success">
                            {row.defaultNeighborhood.name}
                          </span>
                        ) : (
                          <span className="text-muted">Not set</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${row.active ? 'bg-success' : 'bg-danger'}`}>
                          {row.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {row.lastLogin ? (
                          <small>{new Date(row.lastLogin).toLocaleString()}</small>
                        ) : (
                          <small className="text-muted">Never</small>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <CustomButton
                            variant="primaryButton"
                            text="View"
                            onClick={() => navigate(`/admins/view/${row._id}`)}
                          />

                          {isSuperAdmin && (
                            <>
                              <CustomButton
                                variant="warningButton"
                                text="Edit"
                                onClick={() => handleEdit(row)}
                              />

                              {row.active && row._id !== user._id && (
                                <CustomButton
                                  variant="dangerButton"
                                  text="Deactivate"
                                  onClick={() => handleDeactivate(row)}
                                />
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </CustomTable>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        show={showDeactivateModal}
        close={() => setShowDeactivateModal(false)}
        action={confirmDeactivate}
        heading="Deactivate Admin"
      >
        <p>
          Are you sure you want to deactivate <strong>{selectedAdmin?.name}</strong>?
        </p>
        <p className="text-danger">
          They will no longer be able to access the admin panel.
        </p>
      </CustomModal>

      <EditAdminModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        admin={selectedAdmin}
        onSuccess={handleEditSuccess}
      />
    </DashboardLayout>
  )
}

export default AdminManagement
